import { z } from 'zod';

import { idSchema, isoDateTimeSchema } from './common.js';
import { documentTypeSchema } from './document.js';

/**
 * Slugs fixos das 6 páginas institucionais. Não há criação dinâmica —
 * o conjunto é imutável e mapeia 1:1 com as copies aprovadas pelo cliente.
 */
export const contentPageSlugSchema = z.enum([
  'home',
  'institucional',
  'solucoes',
  'idpe-em-numeros',
  'transparencia',
  'publicacoes',
]);
export type ContentPageSlug = z.infer<typeof contentPageSlugSchema>;

export const CONTENT_PAGE_LABELS: Record<ContentPageSlug, string> = {
  home: 'Home',
  institucional: 'Institucional',
  solucoes: 'Soluções',
  'idpe-em-numeros': 'IDPE em Números',
  transparencia: 'Transparência',
  publicacoes: 'Publicações',
};

export const contentPageStatusSchema = z.enum(['rascunho', 'publicado', 'arquivado']);
export type ContentPageStatus = z.infer<typeof contentPageStatusSchema>;

/**
 * CTA reutilizável (botão com label + URL). Aparece em hero, text_section e cta_block.
 */
export const ctaSchema = z.object({
  label: z.string().min(1).max(80),
  href: z.string().min(1).max(500),
  variant: z.enum(['primary', 'secondary', 'link']),
});
export type Cta = z.infer<typeof ctaSchema>;

// ─── Tipos de bloco (discriminated union por `type`) ────────────────────

/**
 * `hero` — bloco de abertura com eyebrow + headline + subheadline + CTAs.
 * Variante visual escolhe entre "padrão" (texto + imagem ou tipografia
 * autoral) e "minimalista" (só texto).
 */
export const heroBlockSchema = z.object({
  id: idSchema,
  type: z.literal('hero'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().min(1).max(280),
    subheadline: z.string().max(600).optional().or(z.literal('')),
    ctas: z.array(ctaSchema).max(2),
    variant: z.enum(['padrao', 'minimal']),
  }),
});

/**
 * `text_section` — seção de texto rico com eyebrow + headline + corpo (HTML).
 * Usada para "Posicionamento", "Propósito", "História e origem", "Conflito
 * de Interesse", etc.
 */
export const textSectionBlockSchema = z.object({
  id: idSchema,
  type: z.literal('text_section'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    corpoHtml: z.string().min(1),
    ctas: z.array(ctaSchema).max(2),
    variant: z.enum(['default', 'centered', 'highlighted']),
  }),
});

/**
 * `cards_grid` — grade de N cards (2/3/4 colunas). Cada card tem
 * título + texto + ícone-slug opcional + CTA opcional. Cobre os blocos
 * "Para quem atuamos", "Soluções", "Cases", "Áreas do projeto", etc.
 */
export const cardItemSchema = z.object({
  titulo: z.string().min(1).max(200),
  texto: z.string().max(800).optional().or(z.literal('')),
  iconSlug: z.string().max(40).optional().or(z.literal('')),
  imageUrl: z.string().max(500).optional().or(z.literal('')),
  cta: ctaSchema.optional(),
});
export type CardItem = z.infer<typeof cardItemSchema>;

export const cardsGridBlockSchema = z.object({
  id: idSchema,
  type: z.literal('cards_grid'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    legenda: z.string().max(400).optional().or(z.literal('')),
    cards: z.array(cardItemSchema).min(1).max(12),
    columns: z.number().int().min(1).max(4),
  }),
});

/**
 * `pillars` — 3-4 pilares numerados com letra/número grande. Cobre
 * "Três pilares" da Metodologia (A/B/C ou 01/02/03).
 */
export const pillarItemSchema = z.object({
  label: z.string().min(1).max(10),
  titulo: z.string().min(1).max(200),
  texto: z.string().max(800),
});
export type PillarItem = z.infer<typeof pillarItemSchema>;

export const pillarsBlockSchema = z.object({
  id: idSchema,
  type: z.literal('pillars'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    subheadline: z.string().max(500).optional().or(z.literal('')),
    pillars: z.array(pillarItemSchema).min(2).max(4),
  }),
});

/**
 * `timeline` — marcos horizontais conectados, usado em "Fases de atuação"
 * e "Fluxo de estruturação". Cada marco tem label + título + descrição.
 */
export const timelineStepSchema = z.object({
  label: z.string().min(1).max(20),
  titulo: z.string().min(1).max(200),
  texto: z.string().max(800).optional().or(z.literal('')),
  tone: z.enum(['default', 'highlight', 'muted']),
});
export type TimelineStep = z.infer<typeof timelineStepSchema>;

export const timelineBlockSchema = z.object({
  id: idSchema,
  type: z.literal('timeline'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    subheadline: z.string().max(500).optional().or(z.literal('')),
    steps: z.array(timelineStepSchema).min(2).max(12),
  }),
});

/**
 * `cta_block` — bloco de fechamento de conversão. Headline + subheadline
 * + 1-2 CTAs em destaque. Usado no final de toda página.
 */
export const ctaBlockSchema = z.object({
  id: idSchema,
  type: z.literal('cta_block'),
  visible: z.boolean(),
  data: z.object({
    headline: z.string().min(1).max(280),
    subheadline: z.string().max(500).optional().or(z.literal('')),
    ctas: z.array(ctaSchema).min(1).max(2),
    variant: z.enum(['dark', 'light']),
  }),
});

/**
 * `faq` — bloco de Perguntas Frequentes (Fase 5.6). Emite Schema.org
 * FAQPage quando presente na página /solucoes. Items mínimos = 2.
 */
export const faqItemSchema = z.object({
  pergunta: z.string().min(1).max(280),
  resposta: z.string().min(1).max(2000),
});
export type FaqItem = z.infer<typeof faqItemSchema>;

export const faqBlockSchema = z.object({
  id: idSchema,
  type: z.literal('faq'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    subheadline: z.string().max(500).optional().or(z.literal('')),
    items: z.array(faqItemSchema).min(2).max(20),
  }),
});

/**
 * `documents_list` — lista os documentos institucionais publicados
 * (`documents` com `visivel=true`). O bloco guarda apenas o cabeçalho e o
 * filtro; os itens vêm da API em tempo de build, então trocar um PDF pelo
 * admin não exige editar a página. Usado em /transparencia.
 */
export const documentsListBlockSchema = z.object({
  id: idSchema,
  type: z.literal('documents_list'),
  visible: z.boolean(),
  data: z.object({
    eyebrow: z.string().max(80).optional().or(z.literal('')),
    headline: z.string().max(280).optional().or(z.literal('')),
    legenda: z.string().max(400).optional().or(z.literal('')),
    /** Filtro opcional por tipo de documento. Ausente = todos os visíveis. */
    tipo: documentTypeSchema.optional(),
  }),
});

/**
 * União discriminada de blocos. O `type` discrimina o shape de `data`.
 */
export const contentBlockSchema = z.discriminatedUnion('type', [
  heroBlockSchema,
  textSectionBlockSchema,
  cardsGridBlockSchema,
  pillarsBlockSchema,
  timelineBlockSchema,
  ctaBlockSchema,
  faqBlockSchema,
  documentsListBlockSchema,
]);
export type ContentBlock = z.infer<typeof contentBlockSchema>;
export type ContentBlockType = ContentBlock['type'];

export const CONTENT_BLOCK_LABELS: Record<ContentBlockType, string> = {
  hero: 'Hero',
  text_section: 'Seção de texto',
  cards_grid: 'Grade de cards',
  pillars: 'Pilares numerados',
  timeline: 'Linha do tempo',
  cta_block: 'Bloco de CTA',
  faq: 'Perguntas Frequentes',
  documents_list: 'Lista de documentos',
};

// ─── Página completa ─────────────────────────────────────────────────────

export const contentPageSchema = z.object({
  slug: contentPageSlugSchema,
  titulo: z.string().min(1).max(200),
  descricao: z.string().nullable(),
  blocos: z.array(contentBlockSchema),
  status: contentPageStatusSchema,
  seoTitle: z.string().max(70).nullable(),
  seoDesc: z.string().max(200).nullable(),
  publicadoEm: isoDateTimeSchema.nullable(),
  criadoEm: isoDateTimeSchema,
  atualizadoEm: isoDateTimeSchema,
});
export type ContentPage = z.infer<typeof contentPageSchema>;

/**
 * Payload do PATCH. Tudo opcional — cliente envia só o que mudou.
 * `blocos` substitui a array inteira (não é patch dentro do array).
 */
export const updateContentPageInputSchema = z.object({
  titulo: z.string().min(1).max(200).optional(),
  descricao: z.string().max(1000).nullable().optional(),
  blocos: z.array(contentBlockSchema).optional(),
  status: contentPageStatusSchema.optional(),
  seoTitle: z.string().max(70).optional().or(z.literal('')),
  seoDesc: z.string().max(200).optional().or(z.literal('')),
  /** Mensagem opcional gravada no snapshot da versão (Git-like commit message). */
  mensagem: z.string().max(500).optional().or(z.literal('')),
});
export type UpdateContentPageInput = z.infer<typeof updateContentPageInputSchema>;

/** Snapshot de versão da página. */
export const contentVersionSchema = z.object({
  id: idSchema,
  pageSlug: contentPageSlugSchema,
  snapshot: z.record(z.unknown()),
  autorId: idSchema.nullable(),
  autorNome: z.string(),
  mensagem: z.string().nullable(),
  criadoEm: isoDateTimeSchema,
});
export type ContentVersion = z.infer<typeof contentVersionSchema>;
